import { useCallback, useEffect, useMemo, useState } from 'react'
import { computeOrderTotal, fmtMoney } from '../lib/pricing'
import { reconcileFreeShirt } from '../lib/promo'
import { SERVICE_LABEL } from '../lib/orders'
import { shortDate } from '../lib/dates'
import { showToast } from './Toast'
import MarkPaidModal from './MarkPaidModal'
import ServicePicker from './ServicePicker'
import { sendInvoice } from '../lib/invoicing'
import {
  addServiceToPickup,
  removeServiceFromPickup,
  listPickupGroup,
  patchPickupGroup,
  savePickupGroupWeights,
  consumePrepaidLbsForOrder,
} from '../lib/admin'
import {
  addOrderItem,
  deleteOrderItem,
  getMemberCycleUsedLbs,
  getService,
  listActiveServices,
  listItemsForService,
  listOrderItems,
} from '../lib/catalog'

// Ticket for a multi-service pickup: ONE screen, one weigh block per order.
//
// Nothing about the data model changes — there is still one order per
// service, each with its own weight, total and return. Only the admin
// screen is grouped, so Sébastien weighs both bags without closing and
// reopening tickets.
//
// Pricing stays untouched: every block runs through computeOrderTotal on
// its own order. The footer is the SUM of those results, never a parallel
// calculation.
export default function GroupOrderPanel({ open, order, onClose, onChanged }) {
  const [orders, setOrders] = useState([])
  const [blocks, setBlocks] = useState({})      // orderId → { service, items, catalog, lbs }
  const [memberLbsUsed, setMemberLbsUsed] = useState(0)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [sendingInvoice, setSendingInvoice] = useState(false)
  const [markPaidOpen, setMarkPaidOpen] = useState(false)
  const [markingPaid, setMarkingPaid] = useState(false)
  const [addPickerFor, setAddPickerFor] = useState(null)

  // "Add service" on the ticket — a customer who also handed over dry
  // cleaning shouldn't need a second pickup. The new order joins THIS group.
  const [services, setServices] = useState([])
  const [addServiceOpen, setAddServiceOpen] = useState(false)
  const [addingService, setAddingService] = useState(false)
  const [confirmRemove, setConfirmRemove] = useState(null) // orderId
  const [removingService, setRemovingService] = useState(null)
  const [confirmCancelAll, setConfirmCancelAll] = useState(false)
  const [cancellingAll, setCancellingAll] = useState(false)

  const groupId = order?.pickup_group_id || null

  const load = useCallback(async () => {
    if (!groupId) return
    setLoading(true)
    try {
      const rows = await listPickupGroup(groupId)
      setOrders(rows)

      const allServices = await listActiveServices().catch(() => [])
      setServices(allServices)
      const dryId = allServices.find((s) => s.key === 'dry_cleaning')?.id || null

      const entries = await Promise.all(rows.map(async (o) => {
        const [svc, items, own, dry] = await Promise.all([
          o.service_id ? getService(o.service_id).catch(() => null) : Promise.resolve(null),
          listOrderItems(o.id).catch(() => []),
          o.service_id ? listItemsForService(o.service_id).catch(() => []) : Promise.resolve([]),
          dryId && dryId !== o.service_id ? listItemsForService(dryId).catch(() => []) : Promise.resolve([]),
        ])
        const seen = new Set()
        const catalog = [...own, ...dry].filter((it) => {
          if (seen.has(it.id)) return false
          seen.add(it.id); return true
        })
        return [o.id, {
          service: svc,
          items,
          catalog,
          lbs: o.weighed_lbs != null ? String(o.weighed_lbs) : '',
        }]
      }))
      setBlocks(Object.fromEntries(entries))

      if (rows[0]?.user_id) {
        getMemberCycleUsedLbs(rows[0].user_id).then(setMemberLbsUsed).catch(() => {})
      }
    } catch (err) {
      showToast(err?.message || 'Could not load the pickup')
    } finally {
      setLoading(false)
    }
  }, [groupId])

  useEffect(() => { if (open && groupId) load() }, [open, groupId, load])

  useEffect(() => {
    if (!open) return
    const onKey = (e) => e.key === 'Escape' && onClose?.()
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [open, onClose])

  // Every block priced on its own order, then summed. Same engine, same
  // rules as a single ticket.
  const previews = useMemo(() => {
    const out = {}
    for (const o of orders) {
      const b = blocks[o.id]
      if (!b?.service) continue
      out[o.id] = computeOrderTotal({
        service: b.service,
        weighedLbs: Number(b.lbs) || 0,
        bags: o.bags,
        items: b.items,
        memberLbsUsedThisMonth: memberLbsUsed,
        serviceDiscount: o.wf_discount_type
          ? { type: o.wf_discount_type, value: o.wf_discount_value }
          : null,
      })
    }
    return out
  }, [orders, blocks, memberLbsUsed])

  const subtotalCents = orders.reduce((s, o) => s + Number(previews[o.id]?.subtotalCents || 0), 0)
  const handlingCents = orders.reduce((s, o) => s + Number(previews[o.id]?.handlingCents || 0), 0)
  const totalCents = subtotalCents + handlingCents

  // Locking is decided for the WHOLE group, not order by order.
  const statuses = [...new Set(orders.map((o) => o.status))]
  const mixedStatuses = statuses.length > 1
  const allProcessing = statuses.length === 1 && statuses[0] === 'processing'
  const anyPaid = orders.some((o) => o.is_paid)
  const editable = !mixedStatuses && allProcessing && !anyPaid

  // Removing a service is about the bag existing at all, not about weighing,
  // so it stays available at EVERY stage until that order has been billed.
  // `editable` is a pricing lock and must not gate it.
  const removable = (o) =>
    !o.is_paid && !o.stripe_invoice_id && o.status !== 'delivered' && o.status !== 'cancelled'
  const canCancelPickup = orders.length > 0 && orders.every(removable)

  const pending = orders.filter(
    (o) => !(Number(blocks[o.id]?.lbs) > 0 || (blocks[o.id]?.items?.length || 0) > 0),
  )
  const persistedTotal = orders.reduce((s, o) => s + Number(o.final_total_cents || 0), 0)
  const isDirty = editable && totalCents !== persistedTotal
  const canCheckout = editable && pending.length === 0
  const blockedReason = !editable
    ? ''
    : pending.length > 0
      ? `Waiting on ${pending.map((o) => o.order_number).join(', ')}`
      : ''

  if (!open || !order) return null
  const head = orders[0] || order

  function setLbs(orderId, value) {
    setBlocks((cur) => ({ ...cur, [orderId]: { ...cur[orderId], lbs: value } }))
  }

  async function refreshItems(orderId) {
    const items = await listOrderItems(orderId).catch(() => [])
    setBlocks((cur) => ({ ...cur, [orderId]: { ...cur[orderId], items } }))
  }

  async function handleAddItem(o, item, quantity) {
    try {
      const bagCount = Number(o.bags) || 1
      await addOrderItem(o.id, item, quantity, {
        bags: bagCount,
        weighedLbs: Number(blocks[o.id]?.lbs) || 0,
      })
      await refreshItems(o.id)
      setAddPickerFor(null)
    } catch (err) {
      showToast(err?.message || 'Could not add the item')
    }
  }

  // Add a whole service to this pickup. The RPC copies the stop from the
  // ticket's head order — we only hand it two ids and a bag count.
  async function handleAddService(svc, bags) {
    if (addingService) return
    setAddingService(true)
    try {
      await addServiceToPickup(head.id, svc.id, bags)
      setAddServiceOpen(false)
      await load()
      onChanged?.({ ...order })
      showToast(`${svc.name} added to this pickup`)
    } catch (err) {
      showToast(err?.message || 'Could not add the service')
    } finally {
      setAddingService(false)
    }
  }

  // Take a service back out of the pickup. The way out when a bag turns out
  // empty and its un-weighed order is holding the whole invoice hostage.
  async function handleRemoveService(o) {
    if (removingService) return
    setRemovingService(o.id)
    try {
      await removeServiceFromPickup(o.id)
      setConfirmRemove(null)
      onChanged?.({ ...order })
      showToast(`${blocks[o.id]?.service?.name || o.order_number} removed from this pickup`)
      // One service left ⇒ the group is gone and this ticket no longer exists.
      onClose?.()
    } catch (err) {
      showToast(err?.message || 'Could not remove the service')
    } finally {
      setRemovingService(null)
    }
  }

  // The whole stop goes away. One write covering every order of the group.
  async function handleCancelPickup() {
    if (cancellingAll) return
    setCancellingAll(true)
    try {
      await patchPickupGroup(groupId, { status: 'cancelled' })
      setConfirmCancelAll(false)
      onChanged?.({ ...order })
      showToast('Pickup cancelled')
      onClose?.()
    } catch (err) {
      showToast(err?.message || 'Could not cancel this pickup')
    } finally {
      setCancellingAll(false)
    }
  }

  async function handleRemoveItem(o, rowId) {
    try {
      await deleteOrderItem(rowId)
      await refreshItems(o.id)
    } catch (err) {
      showToast(err?.message || 'Could not remove the item')
    }
  }

  // One Save for the whole pickup, in a single transaction.
  async function handleSave({ silent = false } = {}) {
    setSaving(true)
    try {
      const rows = orders.map((o) => {
        const p = previews[o.id]

        // Free-shirt promo, per order — never across the group. A pickup is
        // several orders sharing a van, not one big order, and the promo is
        // "3 shirts in ONE order".
        //
        // This has to travel with the totals: the engine already nets the
        // free shirt into lineTotalCents, so writing the total without the
        // discount row would store a bill send-invoice.js cannot explain.
        // Settled orders are left alone, same rule as the single ticket.
        const locked = !!o.paid_at || !!o.stripe_invoice_id
        const promoPatches = locked
          ? new Map()
          : new Map(reconcileFreeShirt(p?.lines || [], p?.freeShirt).map((w) => [w.id, w.patch]))

        const items = (p?.lines || [])
          .filter((l) => l.id && (
            Number(l.line_total_cents) !== Number(l.lineTotalCents) || promoPatches.has(l.id)
          ))
          .map((l) => ({
            id: l.id,
            line_total_cents: l.lineTotalCents,
            ...(promoPatches.get(l.id) || {}),
          }))
        return {
          order_id: o.id,
          weighed_lbs: Number(blocks[o.id]?.lbs) > 0 ? String(Number(blocks[o.id].lbs)) : '',
          final_total_cents: Number(p?.totalCents || 0),
          items,
        }
      })
      await savePickupGroupWeights(rows)

      // Prepaid lbs are debited order by order, in order-number order, using
      // the existing idempotent RPC — not re-implemented here.
      const sorted = [...orders].sort((a, b) =>
        String(a.order_number).localeCompare(String(b.order_number)))
      for (const o of sorted) {
        try { await consumePrepaidLbsForOrder(o.id) }
        catch (err) { console.warn('[group] prepaid debit failed', o.order_number, err) }
      }

      await load()
      onChanged?.({ ...order })
      if (!silent) showToast('Pickup saved')
      return true
    } catch (err) {
      showToast(err?.message || 'Save failed — nothing was written')
      return false
    } finally {
      setSaving(false)
    }
  }

  async function handleSendInvoice() {
    setSendingInvoice(true)
    try {
      const { hosted_invoice_url, reused, auto_paid } = await sendInvoice(head.id)
      showToast(auto_paid
        ? 'Members quota covered — receipt emailed ✅'
        : reused ? 'Invoice already sent — link reused' : 'Invoice sent to customer 📧')
      if (hosted_invoice_url && !auto_paid) window.open(hosted_invoice_url, '_blank', 'noopener')
      onChanged?.({ ...order })
      onClose?.()
    } catch (err) {
      showToast(err?.message || 'Send invoice failed')
    } finally {
      setSendingInvoice(false)
    }
  }

  async function confirmMarkPaid(method) {
    if (!method) return
    setMarkingPaid(true)
    try {
      await patchPickupGroup(groupId, {
        paid_at: new Date().toISOString(),
        paid_method: method,
      })
      setMarkPaidOpen(false)
      onChanged?.({ ...order })
      showToast(method === 'free' ? 'Pickup marked as free' : `Pickup marked as paid — ${method}`)
      onClose?.()
    } catch (err) {
      showToast(err?.message || 'Mark paid failed')
    } finally {
      setMarkingPaid(false)
    }
  }

  const address = [head.address_line1, head.address_line2, head.city, head.state, head.zip]
    .filter(Boolean).join(', ')
  const mapsHref = address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
    : null

  return (
    <div className="fixed inset-0 z-50 flex" role="dialog" aria-modal="true" aria-label="Pickup ticket">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <aside
        className="ml-auto flex h-full w-full max-w-[400px] flex-col overflow-y-auto bg-[#F9FAFB] shadow-2xl"
        style={{ maxWidth: 'min(400px, 92vw)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sticky top-0 z-10 flex items-center justify-between border-b border-line bg-white px-4 py-3">
          <div className="font-bricolage text-[16px] font-extrabold text-navy">
            {orders.map((o) => `#${o.order_number}`).join(' + ') || `#${order.order_number}`}
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="text-[20px] text-ink-3 hover:text-ink">
            ✕
          </button>
        </header>

        <div className="space-y-3 p-4 pb-28">
          {loading && <p className="text-center text-[13px] text-ink-2">Loading pickup…</p>}

          {!loading && mixedStatuses && (
            <section className="rounded-xl border border-red bg-red/10 p-3 text-[12px] font-bold text-red">
              ⚠️ Orders in this pickup are at different stages
              <div className="mt-1 font-normal">
                {orders.map((o) => `${o.order_number}: ${o.status}`).join(' · ')}. Read-only until
                they match.
              </div>
            </section>
          )}

          {/* Client — once, at the top. Never repeated per block. */}
          {!loading && (
            <section className="rounded-xl border border-line bg-white p-4">
              <div className="flex items-baseline justify-between gap-2">
                <div className="font-bricolage text-[15px] font-extrabold text-navy">
                  {head.customer_name || '—'}
                </div>
                <span className="rounded-full bg-navy/10 px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wider text-navy">
                  {orders.length} services
                </span>
              </div>
              {head.customer_is_member && (
                <div className="mt-0.5 text-[11px] font-bold" style={{ color: '#B58A0F' }}>🏆 Member</div>
              )}
              {head.customer_phone && (
                <a href={`tel:${head.customer_phone}`} className="mt-1 block text-[12px] font-bold text-aqua">
                  📞 {head.customer_phone}
                </a>
              )}
              {head.hotel_name && (
                <div className="mt-1 text-[12px] font-bold text-navy">
                  🏨 {head.hotel_name}{head.room_number ? ` · Room ${head.room_number}` : ''}
                </div>
              )}
              {mapsHref && (
                <a href={mapsHref} target="_blank" rel="noopener noreferrer" className="mt-1 block text-[12px] text-aqua hover:underline">
                  📍 {address}
                </a>
              )}
              <div className="mt-1 text-[11px] text-ink-2">
                Pickup {head.pickup_date ? shortDate(head.pickup_date) : '—'} · Return confirmed when everything is ready
              </div>
              {head.notes && (
                <p className="mt-2 rounded-lg px-2 py-1.5 text-[12px]" style={{ background: '#FEF9E7', color: '#7A5E0C' }}>
                  📝 “{head.notes}”
                </p>
              )}
            </section>
          )}

          {/* One weigh block per order */}
          {!loading && orders.map((o) => {
            const b = blocks[o.id] || {}
            const p = previews[o.id]
            // This block is what "Waiting on GL-xxxx" is talking about.
            const isPending = pending.some((x) => x.id === o.id)
            return (
              <section
                key={o.id}
                className={`rounded-xl border bg-white p-4 ${
                  editable && isPending ? 'border-[1.5px] border-gold' : 'border-line'
                }`}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <div className="font-bricolage text-[14px] font-extrabold text-navy">
                    #{o.order_number}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-ink-3">
                    {b.service?.name || SERVICE_LABEL[o.service] || o.service}
                  </div>
                </div>

                {editable && isPending && (
                  <div
                    className="mt-2 rounded-lg px-2 py-1.5 text-[11px] font-bold"
                    style={{ background: '#FEF9E7', color: '#7A5E0C' }}
                  >
                    ⚖️ Not weighed yet — the whole pickup can’t be invoiced until it is.
                  </div>
                )}

                {b.service?.pricing_mode !== 'per_item' && (
                  <div className="mt-3">
                    <label className="field-label">Weighed lbs</label>
                    {editable ? (
                      <input
                        type="number"
                        inputMode="decimal"
                        step="0.1"
                        min="0"
                        value={b.lbs ?? ''}
                        onChange={(e) => setLbs(o.id, e.target.value)}
                        placeholder="e.g. 18.5"
                        className="field-input"
                      />
                    ) : (
                      <div className="text-[13px] font-bold text-navy">
                        {o.weighed_lbs != null ? `${Number(o.weighed_lbs).toFixed(1)} lbs` : '— lbs'}
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-3 flex items-center justify-between">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-ink-2">Items &amp; extras</div>
                  {editable && (b.catalog?.length || 0) > 0 && (
                    <button
                      type="button"
                      onClick={() => setAddPickerFor(addPickerFor === o.id ? null : o.id)}
                      className="text-[11px] font-extrabold text-navy underline"
                    >
                      {addPickerFor === o.id ? 'Cancel' : '+ Add item'}
                    </button>
                  )}
                </div>

                <ul className="mt-1 space-y-1.5">
                  {(p?.baseCents || 0) > 0 && (
                    <LineRow name={b.service?.name || 'Service'} cents={p.baseCents} locked />
                  )}
                  {(p?.overageCents || 0) > 0 && (
                    <LineRow
                      name={`Overage · ${p.overageLbs.toFixed(1)} lbs`}
                      cents={p.overageCents}
                      locked
                    />
                  )}
                  {(p?.lines || []).map((li) => (
                    <LineRow
                      key={li.id}
                      name={`${li.name_snapshot}${li.quantity > 1 ? ` × ${li.quantity}` : ''}`}
                      cents={li.lineTotalCents}
                      onRemove={editable ? () => handleRemoveItem(o, li.id) : undefined}
                    />
                  ))}
                  {(p?.lines || []).length === 0 && (p?.baseCents || 0) === 0 && (
                    <li className="text-[11px] italic text-ink-3">Nothing yet.</li>
                  )}
                </ul>

                {addPickerFor === o.id && editable && (
                  <MiniPicker catalog={b.catalog || []} onPick={(item, qty) => handleAddItem(o, item, qty)} />
                )}

                <div className="mt-3 flex items-center justify-between border-t border-line pt-2 text-[12px]">
                  <span className="text-ink-2">Subtotal</span>
                  <span className="font-bricolage text-[15px] font-extrabold text-navy">
                    {fmtMoney(p?.subtotalCents || 0)}
                  </span>
                </div>

                {/* The bag turned out empty, or the customer changed their
                    mind: take the service out instead of letting it hold the
                    whole invoice. Two taps — this cancels an order. */}
                {removable(o) && orders.length > 1 && (
                  <div className="mt-2 text-right">
                    {confirmRemove === o.id ? (
                      <span className="inline-flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setConfirmRemove(null)}
                          className="text-[11px] font-extrabold text-ink-2 underline"
                        >
                          Keep it
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveService(o)}
                          disabled={removingService === o.id}
                          className="rounded-lg bg-red px-3 py-1.5 text-[11px] font-extrabold text-white disabled:opacity-50"
                        >
                          {removingService === o.id ? 'Removing…' : 'Tap again to cancel it'}
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setConfirmRemove(o.id)}
                        className="text-[11px] font-extrabold text-red underline"
                      >
                        Remove from pickup
                      </button>
                    )}
                  </div>
                )}
              </section>
            )
          })}

          {/* Cancel the whole stop, at any stage before it is billed. */}
          {!loading && canCancelPickup && (
            <section className="rounded-xl border border-line bg-white p-4">
              <div className="text-[10px] font-bold uppercase tracking-wider text-ink-2">
                Cancel this pickup
              </div>
              <p className="mt-1 text-[11px] leading-relaxed text-ink-3">
                Cancels all {orders.length} services at once. To drop just one, use
                “Remove from pickup” on its block above.
              </p>
              {confirmCancelAll ? (
                <div className="mt-2 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setConfirmCancelAll(false)}
                    className="flex-1 rounded-lg border border-line bg-white px-3 py-2 text-[12px] font-extrabold text-ink-2"
                  >
                    Keep it
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelPickup}
                    disabled={cancellingAll}
                    className="flex-1 rounded-lg bg-red px-3 py-2 text-[12px] font-extrabold text-white disabled:opacity-50"
                  >
                    {cancellingAll ? 'Cancelling…' : 'Tap again to cancel'}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmCancelAll(true)}
                  className="mt-2 w-full rounded-lg border-[1.5px] border-red bg-white px-3 py-2 text-[12px] font-extrabold text-red hover:bg-red/5"
                >
                  Cancel pickup
                </button>
              )}
            </section>
          )}

          {/* Add a service to this same pickup — one stop, one ticket. */}
          {!loading && editable && (
            <section className="rounded-xl border border-dashed border-line bg-white p-4">
              {!addServiceOpen ? (
                <button
                  type="button"
                  onClick={() => setAddServiceOpen(true)}
                  disabled={orders.length >= 3}
                  className={`w-full rounded-xl border-[1.5px] border-navy bg-white px-3 py-2.5 font-bricolage text-[13px] font-extrabold text-navy ${
                    orders.length >= 3 ? 'opacity-40' : ''
                  }`}
                >
                  {orders.length >= 3 ? 'Pickup full — 3 services max' : '+ Add service'}
                </button>
              ) : (
                <ServicePicker
                  services={services.filter((s) => !orders.some((o) => o.service_id === s.id))}
                  busy={addingService}
                  onCancel={() => setAddServiceOpen(false)}
                  onPick={handleAddService}
                />
              )}
            </section>
          )}

          {/* Group total */}
          {!loading && orders.length > 0 && (
            <section className="rounded-xl border border-line bg-white p-4 text-[12px]">
              <div className="flex items-center justify-between">
                <span className="text-ink-2">Subtotal</span>
                <span className="font-bold text-navy">{fmtMoney(subtotalCents)}</span>
              </div>
              {handlingCents > 0 && (
                <div className="mt-1 flex items-center justify-between">
                  <span className="text-ink-2">
                    Handling &amp; transportation {Number(head.handling_fee_pct || 12)}%
                  </span>
                  <span className="font-bold text-navy">{fmtMoney(handlingCents)}</span>
                </div>
              )}
              <div className="mt-2 flex items-center justify-between border-t border-line pt-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-ink-2">Total</span>
                <span className="font-bricolage text-[20px] font-extrabold text-navy">
                  {fmtMoney(totalCents)}
                </span>
              </div>
            </section>
          )}
        </div>

        {!loading && editable && (
          <footer className="sticky bottom-0 z-10 border-t border-line bg-white p-3">
            {/* A disabled button with a whisper underneath reads as "broken".
                Say what is blocking, above the buttons, in plain words. */}
            {blockedReason && (
              <div
                className="mb-2 rounded-lg px-3 py-2 text-[11px] font-bold leading-relaxed"
                style={{ background: '#FEF9E7', color: '#7A5E0C' }}
              >
                ⚖️ {blockedReason} — enter its weight (or add an item) to unlock
                Send Invoice and Checkout. If that bag doesn’t exist, use
                “Remove from pickup”.
              </div>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => handleSave()}
                disabled={saving}
                className="flex-1 rounded-xl border border-line bg-white px-3 py-3 font-bricolage text-[13px] font-extrabold text-navy disabled:opacity-60"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                onClick={handleSendInvoice}
                disabled={!canCheckout || isDirty || sendingInvoice}
                className={`flex-1 rounded-xl bg-navy px-3 py-3 font-bricolage text-[13px] font-extrabold text-white ${
                  !canCheckout || isDirty || sendingInvoice ? 'opacity-50' : ''
                }`}
              >
                {sendingInvoice ? 'Sending…' : 'Send Invoice'}
              </button>
            </div>
            <button
              type="button"
              onClick={() => setMarkPaidOpen(true)}
              disabled={!canCheckout || markingPaid}
              className={`mt-2 w-full rounded-xl border border-navy bg-white px-3 py-2.5 text-[12px] font-extrabold text-navy ${
                !canCheckout || markingPaid ? 'opacity-50' : ''
              }`}
            >
              Checkout · mark paid
            </button>
            {isDirty && !blockedReason && (
              <p className="mt-1.5 text-center text-[11px] text-ink-2">
                Save the pickup first — the total shown isn’t what would be invoiced.
              </p>
            )}
          </footer>
        )}
      </aside>

      <MarkPaidModal
        open={markPaidOpen}
        order={{ ...head, final_total_cents: totalCents }}
        busy={markingPaid}
        onClose={() => setMarkPaidOpen(false)}
        onConfirm={confirmMarkPaid}
      />
    </div>
  )
}

function LineRow({ name, cents, locked, onRemove }) {
  return (
    <li className="flex items-center justify-between gap-3 text-[12px]">
      <span className="min-w-0 flex-1 truncate font-bold text-navy">{name}</span>
      <span className="font-extrabold text-navy">{fmtMoney(cents)}</span>
      {locked ? (
        <span title="Computed from weight" className="flex h-7 w-7 shrink-0 items-center justify-center text-[13px] text-ink-3">🔒</span>
      ) : onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove item"
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-line text-[15px] font-extrabold text-red hover:bg-red/10"
        >
          ×
        </button>
      ) : <span className="h-7 w-7 shrink-0" />}
    </li>
  )
}

function MiniPicker({ catalog, onPick }) {
  const [id, setId] = useState('')
  const [qty, setQty] = useState(1)
  const picked = catalog.find((c) => c.id === id)
  return (
    <div className="mt-2 rounded-lg border border-line bg-[#F9FAFB] p-2.5">
      <select
        value={id}
        onChange={(e) => setId(e.target.value)}
        className="w-full rounded-lg border border-line bg-white px-2 py-2 text-[12px]"
      >
        <option value="">— Choose —</option>
        {catalog.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name} — {fmtMoney(c.price_cents)}
          </option>
        ))}
      </select>
      <div className="mt-2 flex items-center gap-2">
        <div className="flex items-center gap-1 rounded-lg border border-line bg-white px-1">
          <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} className="h-7 w-7 text-[13px] font-bold text-navy">−</button>
          <span className="w-6 text-center text-[12px] font-extrabold text-navy">{qty}</span>
          <button type="button" onClick={() => setQty((q) => q + 1)} className="h-7 w-7 text-[13px] font-bold text-navy">+</button>
        </div>
        <button
          type="button"
          disabled={!picked}
          onClick={() => picked && onPick(picked, qty)}
          className={`flex-1 rounded-lg bg-navy px-3 py-2 text-[12px] font-extrabold text-white ${picked ? '' : 'opacity-50'}`}
        >
          Add
        </button>
      </div>
    </div>
  )
}
